import { useEffect, useMemo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import sortBy from 'lodash.sortby';
import ChannelsCreateModal from '@features/ChannelsCreateModal';
import { Input } from '@components/Input';
import { CHANNEL, STATUS } from '@constants/channels';
import { useTranslation, useChannels, usePermissions } from '@hooks';
import { StyledSelect } from './styles';

const PrimaryFields = ({ isView, isEdit, channels, setChannelType, getContactsNumber }) => {
    const [createModal, setCreateModal] = useState({
        open: false,
        step: 1,
        type: CHANNEL.SMS,
        status: STATUS.PENDING,
        qrCode: '',
        liveChatPublicId: null,
    });
    const [selectedChannel, setSelectedChannel] = useState();

    const { t } = useTranslation();
    const { control, setValue, watch } = useFormContext();
    const { getAccessibleChannelIds, getChannelOptions } = useChannels();
    const selectedChannelId = watch('channelId');

    const { hasFullSettings } = usePermissions();

    const availableChannels = getAccessibleChannelIds();
    const channelsOptions = useMemo(() => {
        // INFO: withDisconnected to show disconnected channels in options when editing or viewing
        return sortBy(
            getChannelOptions(
                { out: true, withArchivedSelect: isView, withDisconnected: isEdit || isView },
                availableChannels,
            ),
            ['name'],
        );
    }, [availableChannels]);

    useEffect(() => {
        if (selectedChannelId && !isView) {
            getContactsNumber();
        }
    }, [selectedChannelId]);

    useEffect(() => {
        if (channelsOptions.length > 0) {
            /* INFO: if channel is not in options, set the first one when create,
            on edit we need to show disconnected and unlinked channels
            */
            if (!channelsOptions.find(c => c.value === selectedChannelId) && !isEdit && !isView) {
                setValue('channelId', channelsOptions[0]?.value, { shouldValidate: true });
                setChannelType(channelsOptions[0]?.type || CHANNEL.SMS);
            }
        }
    }, [selectedChannelId, channelsOptions]);

    return (
        <>
            <Controller
                control={control}
                name="title"
                render={({ field, fieldState: { error }, ref }) => (
                    <Input
                        ref={ref}
                        inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                        label={t('CampaignsPage.name')}
                        fullWidth
                        error={!!error}
                        helperText={error ? t(error.message) : null}
                        required
                        disabled={isView}
                        sx={{ gridColumn: '1 / span 2' }}
                    />
                )}
            />
            <Controller
                control={control}
                name="channelId"
                rules={{ required: t('validation.required') }}
                render={({ field, fieldState: { error }, ref }) => (
                    <StyledSelect
                        {...field}
                        onChange={({ target: { value } }) => {
                            if (value !== 'create-new') {
                                const channel = channels.find(c => c.id === value);
                                setChannelType(channel?.type || CHANNEL.SMS);
                                setValue('whatsAppTemplateId', '');
                                setValue('whatsAppTemplateRegistryId', '', { shouldValidate: true });
                                setValue('channelId', value, { shouldValidate: true, shouldDirty: true });
                            }
                        }}
                        ref={ref}
                        label={t('common.channel')}
                        fullWidth
                        options={channelsOptions}
                        required
                        disabled={isView}
                        error={!!error}
                        helperText={error ? t(error.message) : null}
                        creatable={hasFullSettings()}
                        creatableText={t('SettingsChannels.createNewChannel')}
                        creatableClick={() => setCreateModal(prev => ({ ...prev, open: true }))}
                    />
                )}
            />
            {createModal.open ? (
                <ChannelsCreateModal
                    modal={createModal}
                    setModal={setCreateModal}
                    channel={selectedChannel}
                    setChannel={setSelectedChannel}
                />
            ) : null}
        </>
    );
};

export default PrimaryFields;
